import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { fmtDateTime } from "../lib/timezone";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { ListChecks } from "lucide-react";

const TYPE_LABEL = {
  bug: "Bug", feature: "Suggestion", confusing: "Confusing / hard to use",
  general: "General feedback", pilot_support: "Pilot support",
};
const STATUS_LABEL = {
  new: "New", reviewing: "Reviewing", planned: "Planned", in_progress: "In progress",
  resolved: "Resolved", closed: "Closed", not_planned: "Not planned",
};
const STATUS_CLS = {
  new: "bg-blue-100 text-blue-700", reviewing: "bg-indigo-100 text-indigo-700",
  planned: "bg-violet-100 text-violet-700", in_progress: "bg-amber-100 text-amber-700",
  resolved: "bg-emerald-100 text-emerald-700", closed: "bg-slate-200 text-slate-600",
  not_planned: "bg-rose-100 text-rose-700",
};

export default function MyFeedback() {
  const { user } = useAuth();
  const tz = user?.org_timezone;
  const [items, setItems] = useState(null);

  useEffect(() => {
    api.get("/feedback/mine").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);

  return (
    <div className="mx-auto max-w-3xl animate-fade-up space-y-6">
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">Pilot</p>
        <h1 className="font-display text-3xl font-black tracking-tight text-slate-900">My Feedback</h1>
        <p className="mt-1 text-sm text-slate-500">Feedback and bug reports you have submitted.</p>
      </div>

      {items === null ? (
        <div className="h-32 animate-pulse rounded-md bg-slate-200/70" />
      ) : items.length === 0 ? (
        <Card className="flex flex-col items-center rounded-md border border-slate-200 bg-white p-10 text-center shadow-sm" data-testid="my-feedback-empty">
          <ListChecks className="h-8 w-8 text-slate-300" />
          <p className="mt-2 text-sm text-slate-500">You haven't submitted any feedback yet.</p>
        </Card>
      ) : (
        <div className="space-y-3" data-testid="my-feedback-list">
          {items.map((f) => (
            <Card key={f.id} data-testid={`my-feedback-${f.reference}`} className="rounded-md border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-sm font-bold text-slate-900">{f.reference}</span>
                  <Badge variant="outline" className="text-xs">{TYPE_LABEL[f.feedback_type]}</Badge>
                  {f.severity && <Badge variant="outline" className="text-xs capitalize">{f.severity}</Badge>}
                </div>
                <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${STATUS_CLS[f.status]}`}>{STATUS_LABEL[f.status]}</span>
              </div>
              <p className="mt-2 text-sm font-medium text-slate-800">{f.title}</p>
              <p className="mt-1 text-xs text-slate-400">Submitted {fmtDateTime(f.created_at, tz)}</p>
              {f.resolution_note && (
                <div className="mt-2 rounded-md border border-emerald-100 bg-emerald-50/60 p-2.5 text-sm text-emerald-800">
                  <span className="font-semibold">Update: </span>{f.resolution_note}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
